import { Icon } from '@typedream/components';
import { textColorsBasic } from '@typedream/core';
import classNames from 'classnames';
import { CSSProperties, useEffect, useState } from 'react';
import selectMediaStyles from '../SelectMedia.module.css';
import styles from './SelectMediaIcon.module.css';

const iconTypeToClass = {
  regular: 'bx',
  solid: 'bxs',
  logo: 'bxl',
};

const getIconsAsync = async (keyword?: string) => {
  const { default: icons } = await import('./iconsList');

  if (keyword) {
    const lowercasedKeyword = keyword.toLowerCase();
    return icons.filter(
      (i) => i.name.includes(lowercasedKeyword)
        || i.keywords.some((k) => k.includes(lowercasedKeyword)),
    );
  }

  return icons;
};

enum Steps {
  SELECT_ICON,
  SELECT_COLOR,
}

export interface SelectMediaIconProps {
  onSelect: (icon: {
    type: string
    name: string
    color: CSSProperties['color']
  }) => void
}

export function SelectMediaIcon(props: SelectMediaIconProps) {
  const { onSelect } = props;

  const [icons, setIcons] = useState<Array<{ type: string; name: string }>>([]);
  const getIcons = (...args: Parameters<typeof getIconsAsync>) => {
    getIconsAsync(...args).then(setIcons);
  };
  useEffect(getIcons, []);

  const [step, setStep] = useState(Steps.SELECT_ICON);

  const [selectedIcon, setSelectedIcon] = useState<{
    type: string
    name: string
  }>();
  const [selectedColor, setSelectedColor] = useState<keyof typeof textColorsBasic>('default');

  const confirmSelection = () => {
    if (selectedIcon) {
      onSelect({ ...selectedIcon, color: selectedColor });
    }
  };

  return (
    <div>
      <div className={selectMediaStyles.filter}>
        <input
          className={selectMediaStyles.input}
          type="text"
          placeholder="Search icon..."
          onChange={(e) => {
            getIcons(e.target.value);
            setStep(Steps.SELECT_ICON);
          }}
        />
      </div>

      <div
        className={styles.iconList}
        style={{
          display: step === Steps.SELECT_ICON ? 'grid' : 'none',
        }}
      >
        {icons.map((i) => (
          <div
            key={`${i.type}-${i.name}`}
            className={styles.iconContainer}
            onClick={() => {
              setSelectedIcon({ type: i.type, name: i.name });
              setStep(Steps.SELECT_COLOR);
            }}
            role="none"
          >
            <Icon
              key={i.name}
              type={iconTypeToClass[i.type as keyof typeof iconTypeToClass]}
              name={i.name}
              className={styles.icon}
            />
            <span className={styles.iconName}>{i.name}</span>
          </div>
        ))}
      </div>

      <div
        className={styles.iconSelectColor}
        style={{
          display: step === Steps.SELECT_COLOR && selectedIcon ? 'flex' : 'none',
        }}
      >
        <div className="is-flex is-align-items-start">
          <div
            className={styles.iconShowcase}
            style={
              {
                '--color': selectedColor
                  ? textColorsBasic[selectedColor]
                  : undefined,
              } as CSSProperties
            }
            onClick={confirmSelection}
            role="none"
          >
            <Icon
              type={
                iconTypeToClass[
                selectedIcon?.type as keyof typeof iconTypeToClass
                ]
              }
              name={selectedIcon?.name ?? '-'}
              className={styles.icon}
            />
          </div>
          <div>
            <div className={styles.colorPickerContainer}>
              <div className="is-size-5 has-text-weight-bold with-text-color">
                {selectedIcon?.name}
              </div>
              <div className={styles.colorPicker}>
                {Object.entries(textColorsBasic).map(([k, v]) => (
                  <div
                    key={k}
                    className={classNames(styles.color, {
                      [styles.selected]: k === selectedColor,
                    })}
                    style={{ background: v }}
                    onClick={() => setSelectedColor(k as typeof selectedColor)}
                    role="none"
                  />
                ))}
              </div>
            </div>
            <button
              type="button"
              className={styles.useIconButton}
              onClick={confirmSelection}
            >
              Use Icon
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}
